import { type HTMLAttributes, useState } from 'react';
import { kaiFontFamily, styleguideColorsExtended as colors } from '../../../core/tokens';
import { resolveStyleOverride, type StyleOverride } from '../../styleOverrides';
import { kaiTypographyWeb } from '../../typography';
import { CheckIcon, MissingPhotoIcon } from '../Icons';

export type GalleryMediaTileTone = 'default' | 'unsorted';

export interface GalleryMediaTileModel {
  id: string;
  label: string;
  imageUrl?: string;
  alt?: string;
  emptyLabel?: string;
  scopeLabel?: string;
  actorLabel?: string;
  sourceLabel?: string;
  selected?: boolean;
  tone?: GalleryMediaTileTone;
}

export interface GalleryMediaTileProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onSelect'> {
  model: GalleryMediaTileModel;
  onOpen?: (id: string) => void;
  onToggleSelection?: (id: string) => void;
  showSelectionControl?: boolean;
  aspectRatio?: string;
  styleOverride?: StyleOverride;
}

export const GalleryMediaTile = ({
  model,
  onOpen,
  onToggleSelection,
  showSelectionControl = true,
  aspectRatio = '4 / 3',
  styleOverride: tileCss,
  style,
  onMouseEnter,
  onMouseLeave,
  onFocus,
  onBlur,
  ...props
}: GalleryMediaTileProps) => {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const selected = Boolean(model.selected);
  const tone = model.tone ?? 'default';
  const overlayVisible = !selected && (hovered || focused);

  return (
    <div
      data-gallery-media-tile='true'
      data-gallery-selected={selected ? 'true' : 'false'}
      data-gallery-tone={tone}
      onMouseEnter={event => {
        setHovered(true);
        onMouseEnter?.(event);
      }}
      onMouseLeave={event => {
        setHovered(false);
        onMouseLeave?.(event);
      }}
      onFocus={event => {
        setFocused(true);
        onFocus?.(event);
      }}
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
        onBlur?.(event);
      }}
      style={{
        position: 'relative',
        width: '100%',
        minWidth: 0,
        aspectRatio,
        overflow: 'hidden',
        background: colors.stroke,
        color: colors.paper,
        fontFamily: kaiFontFamily,
        outline: selected ? `2.5px solid ${colors.ink}` : 'none',
        outlineOffset: selected ? -2.5 : 0,
        ...style,
        ...resolveStyleOverride(tileCss),
      }}
      {...props}
    >
      <button
        type='button'
        aria-label={`Open ${model.label}`}
        onClick={() => onOpen?.(model.id)}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          border: 0,
          padding: 0,
          background: 'transparent',
          color: 'inherit',
          cursor: onOpen ? 'pointer' : 'default',
          fontFamily: 'inherit',
        }}
      >
        {model.imageUrl
          ? (
            <img
              src={model.imageUrl}
              alt={model.alt ?? model.label}
              style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
            />
          )
          : (
            <span
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                color: colors.textTertiary,
                ...kaiTypographyWeb.xxSmallMed,
              }}
            >
              <MissingPhotoIcon size={24} color='currentColor' aria-hidden='true' />
              {model.emptyLabel ?? 'No photo'}
            </span>
          )}
      </button>

      <span
        data-gallery-media-overlay='true'
        data-gallery-visible={overlayVisible ? 'true' : 'false'}
        aria-hidden='true'
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: '7px 8px',
          opacity: overlayVisible ? 1 : 0,
          pointerEvents: 'none',
          transition: 'opacity 120ms ease',
          background: 'linear-gradient(to bottom, rgba(26,26,26,.5) 0%, rgba(26,26,26,0) 30%, rgba(26,26,26,0) 55%, rgba(26,26,26,.78) 100%)',
        }}
      >
        <span style={{ display: 'flex', justifyContent: 'flex-end', paddingLeft: 26 }}>
          {model.scopeLabel
            ? (
              <span
                style={{
                  padding: '2px 6px',
                  border: `1px solid ${tone === 'unsorted' ? colors.error : 'rgba(255,255,255,.35)'}`,
                  background: tone === 'unsorted' ? colors.error : 'rgba(26,26,26,.55)',
                  color: colors.white,
                  ...kaiTypographyWeb.xxxSmallBold,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                }}
              >
                {model.scopeLabel}
              </span>
            )
            : null}
        </span>
        <span style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
          {model.actorLabel
            ? (
              <span style={{ ...kaiTypographyWeb.xxSmallMed, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {model.actorLabel}
              </span>
            )
            : null}
          {model.sourceLabel
            ? (
              <span
                style={{ ...kaiTypographyWeb.xxxSmall, color: 'rgba(255,255,255,.85)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {model.sourceLabel}
              </span>
            )
            : null}
        </span>
      </span>

      {showSelectionControl
        ? (
          <button
            type='button'
            aria-label={`${selected ? 'Deselect' : 'Select'} ${model.label}`}
            aria-pressed={selected}
            disabled={!onToggleSelection}
            onClick={() => onToggleSelection?.(model.id)}
            style={{
              position: 'absolute',
              top: 5,
              left: 5,
              zIndex: 4,
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              border: `1.5px solid ${selected ? colors.ink : colors.midtone}`,
              background: selected ? colors.ink : 'rgba(255,253,248,.82)',
              color: selected ? colors.white : 'transparent',
              cursor: onToggleSelection ? 'pointer' : 'default',
              opacity: onToggleSelection ? 1 : 0.72,
              transition: 'background 100ms ease, border-color 100ms ease, color 100ms ease',
            }}
          >
            <CheckIcon size={13} color='currentColor' aria-hidden='true' />
          </button>
        )
        : null}
    </div>
  );
};
